import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { api } from '../api'
import { useAktion, useLaden } from '../hooks'
import Kopplungen from '../komponenten/Kopplungen'
import Meldung from '../komponenten/Meldung'
import Meldungen from '../komponenten/Meldungen'
import PlatzInDerKladde from '../komponenten/PlatzInDerKladde'
import { menge, wochen, zahlLesen, zahlText } from '../format'
import { mvnAusGewicht, ohneMvn } from '../hilfen'
import type { Artikel, ArtikelEingabe, Artikelart } from '../typen'

/**
 * Artikelliste mit der MVN. Ohne MVN laesst sich eine Stueckzahl nicht in Tonnen umrechnen -
 * deshalb steht hier auch, woher der Faktor stammt.
 *
 * Anlegen und Aendern laufen ueber dasselbe Formular. Es steht ueber der Tabelle statt in einem
 * Dialog: wer eine MVN eintraegt, will meist gleichzeitig sehen, welchen Faktor der Artikel
 * daneben hat.
 */

const LEER: ArtikelEingabe = {
  artikelnummer: '',
  bezeichnung: '',
  art: 'material',
  gebindegewicht_kg: null,
  mvn: null,
  einheit: 'Stk',
  mvn_begruendung: null,
  gebindegruppe: null,
  hersteller: null,
  lieferzeit_tage: null,
  mindestbestand_t: null,
  plan_t_je_monat: null,
  aktiv: true,
  kurzname: null,
}

/** Der Platz in der Kladde geht nicht mit: er wird über „Einordnen" gesetzt, und das Formular soll ihn nicht mit
 *  einem alten Stand überschreiben. */
function alsEingabe(artikel: Artikel): ArtikelEingabe {
  const { id: _id, reihenfolge: _platz, ...rest } = artikel
  return rest
}

/**
 * Die Zahlenfelder des Formulars. Sie halten Text, bis gespeichert wird (Gestaltungsregel in der
 * CLAUDE.md): würde „abc" schon beim Tippen gelesen, stünde stumm null im Artikel - so ist am
 * 17.09.2026 in der Sandbox eine MVN verschwunden.
 */
type Zahlfeld = 'gebindegewicht_kg' | 'mvn' | 'mindestbestand_t' | 'plan_t_je_monat'
type Zahltexte = Record<Zahlfeld, string>

const ZAHLFELDER: { name: Zahlfeld; titel: string }[] = [
  { name: 'gebindegewicht_kg', titel: 'Gebinde in kg' },
  { name: 'mvn', titel: 'MVN je t' },
  { name: 'mindestbestand_t', titel: 'Mindestbestand in t' },
  { name: 'plan_t_je_monat', titel: 'Planmenge t je Monat' },
]

/**
 * Der Puffer vor der Nachauflage je Artikel (02.11.2026) steht in Wochen, wie Frau Barth rechnet
 * („930198 und 930270 mit 8 Wochen"), gespeichert wird er in Tagen. Eine bloße Zahl sind Wochen,
 * „10 Tage" bleibt Tage, „8 Wo" Wochen. Was nicht glatt in Wochen aufgeht, steht als „10 Tage" im Feld,
 * damit beim Speichern nichts gerundet wird.
 */
function pufferText(tage: number | null | undefined): string {
  if (tage === null || tage === undefined) return ''
  return tage % 7 === 0 ? String(tage / 7) : `${tage} Tage`
}

/** Lesen wie `pufferText` schreibt; leer = null (Wert des Lieferanten), Unlesbares = NaN. */
function pufferLesen(text: string): number | null {
  const t = text.trim().toLowerCase()
  if (t === '') return null
  const tage = t.match(/^(\d+)\s*(tage|tag)$/)
  if (tage) return Number(tage[1])
  const w = zahlLesen(t.replace(/\s*(wochen|woche|wo|w)$/, ''))
  return w === null ? Number.NaN : Math.round(w * 7)
}

function zahltexteAus(eingabe: ArtikelEingabe): Zahltexte {
  return {
    gebindegewicht_kg: zahlText(eingabe.gebindegewicht_kg),
    mvn: zahlText(eingabe.mvn),
    mindestbestand_t: zahlText(eingabe.mindestbestand_t),
    plan_t_je_monat: zahlText(eingabe.plan_t_je_monat),
  }
}

/**
 * Die Zahlenfelder lesen. Zurück kommen entweder die Werte oder die Fehler in Worten - ein Feld mit
 * Text, der keine Zahl ist, und ein negativer Wert werden beide genannt, nicht stumm verworfen.
 */
function zahlenLesen(texte: Zahltexte): { werte: Partial<Record<Zahlfeld, number | null>>; fehler: string[] } {
  const werte: Partial<Record<Zahlfeld, number | null>> = {}
  const fehler: string[] = []
  for (const { name, titel } of ZAHLFELDER) {
    const text = texte[name].trim()
    const wert = zahlLesen(text)
    if (text !== '' && wert === null) {
      fehler.push(`${titel}: „${text}" ist keine Zahl - bitte eine Zahl eintragen oder das Feld leer lassen.`)
    } else if (wert !== null && wert < 0) {
      fehler.push(`${titel}: „${text}" ist negativ - bitte eine Zahl ab 0 eintragen oder das Feld leer lassen.`)
    } else {
      werte[name] = wert
    }
  }
  return { werte, fehler }
}

export default function StammdatenSeite() {
  // Ein Link auf einen Artikel (`artikelPfad`, z.B. von der Lieferantenseite) filtert die Liste auf seine Nummer.
  const [suchparameter] = useSearchParams()
  const [suche, setSuche] = useState(() => suchparameter.get('suche') ?? '')
  // „nur diese zeigen" aus dem Hinweis: nur disponierte Materialien ohne MVN.
  const [nurOhneMvn, setNurOhneMvn] = useState(false)
  const [formular, setFormular] = useState<ArtikelEingabe | null>(null)
  const [zahltexte, setZahltexte] = useState<Zahltexte>(zahltexteAus(LEER))
  const [puffer, setPuffer] = useState('')
  const [bearbeitet, setBearbeitet] = useState<string | null>(null)
  // Alle Kopplungen über der Tabelle - je Artikel stehen sie auch im Formular.
  const [kopplungenOffen, setKopplungenOffen] = useState(false)
  const { daten, fehler: ladeFehler, neuLaden: laden } = useLaden<Artikel[]>(() => api.artikel(), [])
  const artikel = daten ?? []
  const { fehler, erfolg, arbeitet, setFehler, setErfolg, ausfuehren } = useAktion()

  function feld(felder: Partial<ArtikelEingabe>) {
    setFormular((alt) => (alt ? { ...alt, ...felder } : alt))
  }

  function zahltext(name: Zahlfeld, text: string) {
    setZahltexte((alt) => ({ ...alt, [name]: text }))
  }

  /** Formular und Zahlentexte gemeinsam öffnen oder schließen - sie gehören zusammen. */
  function formularOeffnen(eingabe: ArtikelEingabe | null) {
    setFormular(eingabe)
    setZahltexte(zahltexteAus(eingabe ?? LEER))
    setPuffer(pufferText(eingabe?.puffer_tage))
  }

  /**
   * Aus dem Gebindegewicht folgt die MVN: 1.000 kg geteilt durch das Gewicht. Ein leeres oder
   * unlesbares Gewicht lässt die MVN stehen - nur ein brauchbares Gewicht schreibt sie neu.
   */
  function gewichtSetzen(wert: string) {
    zahltext('gebindegewicht_kg', wert)
    const abgeleitet = mvnAusGewicht(zahlLesen(wert))
    if (abgeleitet.mvn !== undefined) {
      zahltext('mvn', zahlText(abgeleitet.mvn))
      feld({ mvn_begruendung: abgeleitet.mvn_begruendung })
    }
  }

  async function speichern() {
    if (!formular) return
    if (!formular.artikelnummer.trim() || !formular.bezeichnung.trim()) {
      setFehler('Artikelnummer und Bezeichnung müssen ausgefüllt sein.')
      return
    }
    const { werte, fehler: zahlfehler } = zahlenLesen(zahltexte)
    // Der Puffer gilt nur für Material, verpackungsfrei nur für Fertigartikel - das andere bleibt leer.
    const pufferTage = formular.art === 'material' ? pufferLesen(puffer) : null
    if (pufferTage !== null && (!Number.isFinite(pufferTage) || pufferTage < 0)) {
      zahlfehler.push(`Puffer vor Nachauflage: „${puffer.trim()}“ ist keine Wochenzahl - z. B. 8 oder „10 Tage“, leer = Wert des Lieferanten.`)
    }
    if (zahlfehler.length > 0) {
      setFehler(zahlfehler.join(' '))
      return
    }
    // Eine vorhandene MVN zu leeren ist meist ein Versehen - und danach lässt sich das Material nicht
    // mehr in Tonnen rechnen, Importe dafür werden gesperrt. Deshalb eine Rückfrage, keine Sperre.
    const alteMvn = bearbeitet ? (artikel.find((a) => a.artikelnummer === bearbeitet)?.mvn ?? null) : null
    if (alteMvn !== null && werte.mvn === null) {
      const weiter = window.confirm(
        `MVN von ${menge(alteMvn)} löschen? Ohne MVN lässt sich das Material nicht in Tonnen umrechnen.`,
      )
      if (!weiter) return
    }
    const eingabe: ArtikelEingabe = {
      ...formular,
      ...werte,
      puffer_tage: pufferTage,
      verpackungsfrei: formular.art === 'fertigartikel' && formular.verpackungsfrei === true,
    }
    await ausfuehren(async () => {
      if (bearbeitet) {
        await api.artikelAendern(bearbeitet, eingabe)
        setErfolg(`${eingabe.artikelnummer} geändert.`)
      } else {
        await api.artikelAnlegen(eingabe)
        setErfolg(`${eingabe.artikelnummer} angelegt.`)
      }
      formularOeffnen(null)
      setBearbeitet(null)
      laden()
    })
  }

  const begriff = suche.trim().toLowerCase()
  const gefiltert = artikel.filter(
    (a) =>
      (!nurOhneMvn || ohneMvn(a)) &&
      (!begriff ||
        a.artikelnummer.toLowerCase().includes(begriff) ||
        a.bezeichnung.toLowerCase().includes(begriff) ||
        (a.kurzname ?? '').toLowerCase().includes(begriff) ||
        (a.gebindegruppe ?? '').toLowerCase().includes(begriff)),
  )

  function bearbeiten(eintrag: Artikel) {
    formularOeffnen(alsEingabe(eintrag))
    setBearbeitet(eintrag.artikelnummer)
    setFehler(null)
  }

  const anzahlOhneMvn = artikel.filter(ohneMvn).length
  const bearbeiteterArtikel = bearbeitet ? (artikel.find((a) => a.artikelnummer === bearbeitet) ?? null) : null

  return (
    <>
      <h2>Stammdaten</h2>
      <div className="leiste">
        <input
          type="text"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          placeholder="Artikelnummer, Bezeichnung oder Gruppe"
          style={{ minWidth: 280 }}
        />
        <button
          className="haupt"
          onClick={() => {
            formularOeffnen({ ...LEER })
            setBearbeitet(null)
            setFehler(null)
          }}
        >
          Neuer Artikel
        </button>
        <button aria-expanded={kopplungenOffen} onClick={() => setKopplungenOffen((o) => !o)}>
          {kopplungenOffen ? 'Kopplungen schließen' : 'Kopplungen'}
        </button>
        <span className="hinweis">
          {gefiltert.length} von {artikel.length} Artikeln
          {nurOhneMvn && (
            <>
              {' '}
              - nur ohne MVN.{' '}
              <button className="leise" onClick={() => setNurOhneMvn(false)}>
                alle zeigen
              </button>
            </>
          )}
        </span>
      </div>

      <Meldungen fehler={ladeFehler ?? fehler} erfolg={erfolg} />

      {anzahlOhneMvn > 0 && (
        <Meldung art="warnung" titel="Fehlende Umrechnung">
          {anzahlOhneMvn} disponierte Material-Artikel haben keine MVN. Deren Mengen lassen sich nicht in
          Tonnen umrechnen, entsprechende Importe werden gesperrt.{' '}
          {!nurOhneMvn && (
            <button className="leise" onClick={() => setNurOhneMvn(true)}>
              nur diese zeigen
            </button>
          )}
        </Meldung>
      )}

      {kopplungenOffen && (
        <div className="karte" style={{ marginBottom: 16 }}>
          <h3>Kopplungen: welcher Fertigartikel welches Material zieht</h3>
          <Kopplungen alleArtikel={artikel} />
        </div>
      )}

      {formular && (
        <div className="karte" style={{ marginBottom: 16 }}>
          <h3>{bearbeitet ? `Artikel ${bearbeitet} ändern` : 'Neuen Artikel anlegen'}</h3>
          <div className="leiste" style={{ flexWrap: 'wrap' }}>
            <label className="hinweis">
              Artikelnummer{' '}
              <input
                type="text"
                style={{ width: 100 }}
                value={formular.artikelnummer}
                disabled={Boolean(bearbeitet)}
                onChange={(e) => feld({ artikelnummer: e.target.value })}
              />
            </label>
            <label className="hinweis">
              Bezeichnung{' '}
              <input
                type="text"
                style={{ width: 260 }}
                value={formular.bezeichnung}
                onChange={(e) => feld({ bezeichnung: e.target.value })}
              />
            </label>
            <label className="hinweis" title="Steht in den Kopfzeilen der Excel-Exporte statt der Bezeichnung">
              Kurzname (Kopfzeile der Exporte){' '}
              <input
                type="text"
                style={{ width: 200 }}
                value={formular.kurzname ?? ''}
                placeholder="leer = Bezeichnung"
                onChange={(e) => feld({ kurzname: e.target.value || null })}
              />
            </label>
            <label className="hinweis">
              Art{' '}
              <select
                value={formular.art}
                onChange={(e) => feld({ art: e.target.value as Artikelart })}
              >
                <option value="material">Material</option>
                <option value="fertigartikel">Fertigartikel</option>
              </select>
            </label>
            <label className="hinweis">
              Einheit{' '}
              <select value={formular.einheit} onChange={(e) => feld({ einheit: e.target.value })}>
                <option value="Stk">Stk</option>
                <option value="m">m</option>
                <option value="kg">kg</option>
                <option value="t">t</option>
              </select>
            </label>
            <label className="hinweis">
              Gebinde in kg{' '}
              <input
                type="text"
                style={{ width: 80, textAlign: 'right' }}
                value={zahltexte.gebindegewicht_kg}
                inputMode="decimal"
                onChange={(e) => gewichtSetzen(e.target.value)}
              />
            </label>
            <label className="hinweis">
              MVN je t{' '}
              <input
                type="text"
                style={{ width: 90, textAlign: 'right' }}
                value={zahltexte.mvn}
                inputMode="decimal"
                onChange={(e) => zahltext('mvn', e.target.value)}
              />
            </label>
            <label className="hinweis">
              Gruppe{' '}
              <input
                type="text"
                style={{ width: 140 }}
                value={formular.gebindegruppe ?? ''}
                onChange={(e) => feld({ gebindegruppe: e.target.value || null })}
              />
            </label>
            <label className="hinweis">
              Hersteller{' '}
              <input
                type="text"
                style={{ width: 140 }}
                value={formular.hersteller ?? ''}
                onChange={(e) => feld({ hersteller: e.target.value || null })}
              />
            </label>
            <label className="hinweis">
              Mindestbestand in t{' '}
              <input
                type="text"
                style={{ width: 90, textAlign: 'right' }}
                value={zahltexte.mindestbestand_t}
                inputMode="decimal"
                onChange={(e) => zahltext('mindestbestand_t', e.target.value)}
              />
            </label>
            {formular.art === 'fertigartikel' && (
              <label className="hinweis">
                Planmenge t je Monat{' '}
                <input
                  type="text"
                  style={{ width: 90, textAlign: 'right' }}
                  value={zahltexte.plan_t_je_monat}
                  inputMode="decimal"
                  onChange={(e) => zahltext('plan_t_je_monat', e.target.value)}
                />
              </label>
            )}
            {formular.art === 'fertigartikel' && (
              <label className="hinweis" title="Lose Ware ohne Verpackung - braucht keine Gruppe und zieht kein Material">
                <input
                  type="checkbox"
                  checked={formular.verpackungsfrei === true}
                  onChange={(e) => feld({ verpackungsfrei: e.target.checked })}
                />{' '}
                verpackungsfrei (kein Verpackungsmaterial, z. B. IBC)
              </label>
            )}
            {formular.art === 'material' && (
              <label
                className="hinweis"
                title="Nur für diesen Artikel; leer gilt der Puffer des Lieferanten. Eine Zahl sind Wochen, „10 Tage“ geht auch."
              >
                Puffer vor Nachauflage (Wochen){' '}
                <input
                  type="text"
                  style={{ width: 70, textAlign: 'right' }}
                  value={puffer}
                  inputMode="decimal"
                  placeholder="Lieferant"
                  onChange={(e) => setPuffer(e.target.value)}
                />
                {puffer.trim() !== '' && Number.isFinite(pufferLesen(puffer) ?? Number.NaN) && <> = {pufferLesen(puffer)} Tage</>}
              </label>
            )}
            <label className="hinweis">
              <input
                type="checkbox"
                checked={formular.aktiv}
                onChange={(e) => feld({ aktiv: e.target.checked })}
              />{' '}
              disponieren
            </label>
          </div>
          <div className="leiste" style={{ marginTop: 12 }}>
            <button className="haupt" disabled={arbeitet} onClick={() => void speichern()}>
              Speichern
            </button>
            <button
              disabled={arbeitet}
              onClick={() => {
                formularOeffnen(null)
                setBearbeitet(null)
              }}
            >
              Abbrechen
            </button>
            <span className="hinweis">
              Kurzname z. B. OTHÜNA SANNA, Kokos-Wickler; leer = Bezeichnung. Ohne Haken bei disponieren
              zählt der Artikel nicht zur Kladde - für Rohstoffe, die zwar in den Reports stehen, aber keine
              Verpackung sind. Puffer vor Nachauflage leer: es gilt der Wert des Lieferanten.
            </span>
          </div>
          {bearbeiteterArtikel && (
            <>
              <h4>Platz in der Kladde</h4>
              <PlatzInDerKladde artikel={bearbeiteterArtikel} alleArtikel={artikel} onGeaendert={laden} />
              <h4>Kopplungen</h4>
              <Kopplungen key={bearbeiteterArtikel.artikelnummer} artikel={bearbeiteterArtikel} alleArtikel={artikel} />
            </>
          )}
        </div>
      )}

      {/* Artikel, Bezeichnung und „Bearbeiten" bleiben beim Scrollen stehen (stamm-spalte-1/2/3),
          und der Rahmen hat keine eigene Höhe: die Seite scrollt, nicht ein Kasten in der Seite. */}
      <div className="tabellenrahmen stammdaten-rahmen">
        <table className="stammdaten-tabelle">
          <thead>
            <tr>
              <th className="stamm-spalte-1">Artikel</th>
              <th className="stamm-spalte-2">Bezeichnung</th>
              <th className="stamm-spalte-3"></th>
              <th>Art</th>
              <th>Gruppe</th>
              <th className="zahl" title="Spalte in der Kladde - setzen über Bearbeiten, „Einordnen nach Artikel“">
                Spalte Kladde
              </th>
              <th className="zahl">Gebinde in kg</th>
              <th className="zahl">MVN je t</th>
              <th>Einheit</th>
              <th>Herkunft der MVN</th>
              <th>Hersteller</th>
              <th className="zahl">Mindestbestand in t</th>
              <th className="zahl">Planmenge t/Monat</th>
              <th>disponiert</th>
            </tr>
          </thead>
          <tbody>
            {gefiltert.map((eintrag) => (
              <tr key={eintrag.id} onDoubleClick={() => bearbeiten(eintrag)}>
                <td className="stamm-spalte-1">{eintrag.artikelnummer}</td>
                <td className="stamm-spalte-2">
                  {eintrag.bezeichnung}
                  {/* Der Kurzname steht unter der Bezeichnung, nicht in einer eigenen Spalte: er gehört zu ihr
                      und die Tabelle ist schon breit. */}
                  {eintrag.kurzname && <div className="stamm-kurzname">Kurzname: {eintrag.kurzname}</div>}
                </td>
                <td className="stamm-spalte-3">
                  <button onClick={() => bearbeiten(eintrag)}>Bearbeiten</button>
                </td>
                <td>{eintrag.art}</td>
                <td>
                  {eintrag.gebindegruppe ?? ''}
                  {eintrag.verpackungsfrei && <div className="hinweis">verpackungsfrei</div>}
                  {eintrag.puffer_tage != null && (
                    <div className="hinweis" title="Puffer vor Nachauflage nur für diesen Artikel">
                      Puffer {wochen(eintrag.puffer_tage)}
                    </div>
                  )}
                </td>
                <td className="zahl">{eintrag.reihenfolge ?? ''}</td>
                <td className="zahl">{menge(eintrag.gebindegewicht_kg)}</td>
                <td
                  className={`zahl ${ohneMvn(eintrag) ? 'unter-mindestbestand' : ''}`}
                >
                  {/* Fertigartikel werden direkt in Tonnen geführt und brauchen keine MVN. */}
                  {eintrag.mvn
                    ? menge(eintrag.mvn)
                    : eintrag.art === 'material' && eintrag.aktiv
                      ? 'fehlt'
                      : 'nicht nötig'}
                </td>
                <td>{eintrag.einheit}</td>
                <td>{eintrag.mvn_begruendung ?? ''}</td>
                <td>{eintrag.hersteller ?? ''}</td>
                <td className="zahl">{menge(eintrag.mindestbestand_t)}</td>
                <td className="zahl">{menge(eintrag.plan_t_je_monat)}</td>
                <td>{eintrag.aktiv ? 'ja' : 'nein'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {gefiltert.length === 0 && <p className="leer">Keine Artikel gefunden.</p>}
      </div>
    </>
  )
}
